import i18next from "i18next";
import { InitConnection } from "#/requests";
import { useCartStore } from "#/store/cart";
import { useMainStore } from "#/store/main";
import type { CreateOrderAndOpenSocketConfig, PaymentConfig, PaymentErrorHandler } from "#/types/payment";
import { applyOutOfStockToCart, type OutOfStockItem } from "#/utils/applyOutOfStock";
import {
	checkCloseWebSocket,
	checkOrderStatus,
	checkResetBonuses,
	checkResetDiscounts,
	clearSavedData,
	restoreCart,
	restoreUserData,
} from "../handlers/basePaymentHandler";

const extractUserFriendlyMessage = (error: any): string | null => {
	return error?.response?.data?.user_friendly_message || null;
};

export function createPaymentErrorHandler(): PaymentErrorHandler {
	const storeCart = useCartStore();

	const resetPaymentProcces = (config: PaymentConfig): void => {
		checkCloseWebSocket(config);
		config.ws?.close?.(true);
		config.resetStores();
		checkResetBonuses(config);
		checkResetDiscounts(config);
		const isAdultConfirmed = restoreCart(config);
		restoreUserData();
		clearSavedData();
		checkOrderStatus(config, null);
		storeCart.isAdultConfirmed = storeCart.isAdultConfirmed || isAdultConfirmed;
		storeCart.isButtonDisabled = false;
		storeCart.isCreateButtonVisible = true;
	};

	return {
		handlePaymentError(error: any, config: PaymentConfig): void {
			const userMessage = extractUserFriendlyMessage(error);
			if (userMessage) config.showNotification(userMessage);
			resetPaymentProcces(config);
		},

		handleOrderPayError(error: any, config: PaymentConfig): void {
			const userMessage = extractUserFriendlyMessage(error);
			// @tolgee-key { key: 'payment.notification.order-pay-error', ns: 'translation' }
			if (userMessage) config.showNotification(userMessage || i18next.t(`payment.notification.order-pay-error`));
			resetPaymentProcces(config);
		},

		handleOrderError(data: any, config: PaymentConfig): void {
			const connection = InitConnection.create();
			connection
				.getOrder(data.params.order_id)
				.then((response: any) => {
					// @tolgee-key { key: 'payment.notification.order-error', ns: 'translation' }
					const userMessage = response?.user_error_message || i18next.t(`payment.notification.order-error`);
					config.showNotification(userMessage);
				})
				.catch((error: any) => {
					console.error("[payment](handleOrderError)", error);
					// @tolgee-key { key: 'payment.notification.order-error', ns: 'translation' }
					config.showNotification(i18next.t(`payment.notification.order-error`));
				})
				.finally(() => {
					resetPaymentProcces(config);
				});
		},

		handleCreateOrderError(error: any, config: CreateOrderAndOpenSocketConfig): void {
			const responseData = error?.response?.data;

			if (responseData?.extra?.action === "RemoveOutOfStockItems") {
				const outOfStockItems: OutOfStockItem[] = responseData?.extra?.data?.out_of_stock_items ?? [];
				const storeMain = useMainStore();

				applyOutOfStockToCart(storeMain.cartProducts_lib, outOfStockItems);
				storeMain.updateCartInLocalStorage();

				if (responseData.user_friendly_message) config.showNotification(responseData.user_friendly_message);
				return;
			}
			const userMessage = extractUserFriendlyMessage(error);

			// @tolgee-key { key: 'payment.notification.create-order-error', ns: 'translation' }
			config.showNotification(userMessage || i18next.t(`payment.notification.create-order-error`));
		},
	};
}
